import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  ForecastDimension,
  ForecastDimensionValue,
  ForecastLineItem,
  ForecastOverride,
  calculateForecast,
  ForecastCalculation
} from '../utils/forecastEngine';

export interface ForecastScenario {
  id: string;
  user_id: string;
  name: string;
  start_date: string;
  years_count: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

export function useForecastScenarios() {
  const [scenarios, setScenarios] = useState<ForecastScenario[]>([]);
  const [loading, setLoading] = useState(true);

  const loadScenarios = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('forecast_scenarios')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setScenarios(data || []);
    } catch (error) {
      console.error('Error loading forecast scenarios:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadScenarios();
  }, []);

  const createScenario = async (scenario: Partial<ForecastScenario>) => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('forecast_scenarios')
        .insert({
          user_id: user.user.id,
          name: scenario.name || 'Nouveau scénario',
          start_date: scenario.start_date || new Date().toISOString().split('T')[0],
          years_count: scenario.years_count || 5,
          currency: scenario.currency || 'EUR'
        })
        .select()
        .single();

      if (error) throw error;
      await loadScenarios();
      return data;
    } catch (error) {
      console.error('Error creating scenario:', error);
      throw error;
    }
  };

  const updateScenario = async (id: string, updates: Partial<ForecastScenario>) => {
    try {
      const { error } = await supabase
        .from('forecast_scenarios')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await loadScenarios();
    } catch (error) {
      console.error('Error updating scenario:', error);
      throw error;
    }
  };

  const deleteScenario = async (id: string) => {
    try {
      const { error } = await supabase
        .from('forecast_scenarios')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadScenarios();
    } catch (error) {
      console.error('Error deleting scenario:', error);
      throw error;
    }
  };

  return {
    scenarios,
    loading,
    loadScenarios,
    createScenario,
    updateScenario,
    deleteScenario
  };
}

export function useForecastData(scenarioId: string | null) {
  const [scenario, setScenario] = useState<ForecastScenario | null>(null);
  const [dimensions, setDimensions] = useState<ForecastDimension[]>([]);
  const [dimensionValues, setDimensionValues] = useState<ForecastDimensionValue[]>([]);
  const [lineItems, setLineItems] = useState<ForecastLineItem[]>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [calculation, setCalculation] = useState<ForecastCalculation | null>(null);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    if (!scenarioId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [scenarioRes, dimensionsRes, dimValuesRes, lineItemsRes, overridesRes] =
        await Promise.all([
          supabase.from('forecast_scenarios').select('*').eq('id', scenarioId).maybeSingle(),
          supabase.from('forecast_dimensions').select('*').eq('scenario_id', scenarioId),
          supabase.from('forecast_dimension_values').select('*').eq('scenario_id', scenarioId),
          supabase
            .from('forecast_line_items')
            .select('*')
            .eq('scenario_id', scenarioId)
            .order('sort_order'),
          supabase.from('forecast_overrides').select('*')
        ]);

      if (scenarioRes.error) throw scenarioRes.error;
      if (dimensionsRes.error) throw dimensionsRes.error;
      if (dimValuesRes.error) throw dimValuesRes.error;
      if (lineItemsRes.error) throw lineItemsRes.error;
      if (overridesRes.error) throw overridesRes.error;

      setScenario(scenarioRes.data);
      setDimensions(dimensionsRes.data || []);
      setDimensionValues(dimValuesRes.data || []);
      setLineItems(lineItemsRes.data || []);
      setOverrides(overridesRes.data || []);

      // Calculer les montants
      if (scenarioRes.data) {
        const calc = calculateForecast(
          lineItemsRes.data || [],
          dimValuesRes.data || [],
          overridesRes.data || [],
          scenarioRes.data.years_count
        );
        setCalculation(calc);
      }
    } catch (error) {
      console.error('Error loading forecast data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [scenarioId]);

  // Dimensions
  const createDimension = async (dimension: Omit<ForecastDimension, 'id'> & { scenario_id: string }) => {
    try {
      const { data, error } = await supabase
        .from('forecast_dimensions')
        .insert(dimension)
        .select()
        .single();

      if (error) throw error;
      await loadAll();
      return data;
    } catch (error) {
      console.error('Error creating dimension:', error);
      throw error;
    }
  };

  const updateDimension = async (id: string, updates: Partial<ForecastDimension>) => {
    try {
      const { error } = await supabase
        .from('forecast_dimensions')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await loadAll();
    } catch (error) {
      console.error('Error updating dimension:', error);
      throw error;
    }
  };

  const deleteDimension = async (id: string) => {
    try {
      const { error } = await supabase.from('forecast_dimensions').delete().eq('id', id);

      if (error) throw error;
      await loadAll();
    } catch (error) {
      console.error('Error deleting dimension:', error);
      throw error;
    }
  };

  // Dimension values
  const setDimensionValue = async (
    dimensionId: string,
    yearIndex: number,
    value: number
  ) => {
    if (!scenarioId) return;

    try {
      const { error } = await supabase.from('forecast_dimension_values').upsert({
        scenario_id: scenarioId,
        dimension_id: dimensionId,
        year_index: yearIndex,
        value: value
      });

      if (error) throw error;
      await loadAll();
    } catch (error) {
      console.error('Error setting dimension value:', error);
      throw error;
    }
  };

  // Line items
  const createLineItem = async (
    lineItem: Omit<ForecastLineItem, 'id' | 'sort_order'> & { scenario_id: string }
  ) => {
    try {
      const maxOrder = Math.max(...lineItems.map(li => li.sort_order), 0);

      const { data, error } = await supabase
        .from('forecast_line_items')
        .insert({ ...lineItem, sort_order: maxOrder + 1 })
        .select()
        .single();

      if (error) throw error;
      await loadAll();
      return data;
    } catch (error) {
      console.error('Error creating line item:', error);
      throw error;
    }
  };

  const updateLineItem = async (id: string, updates: Partial<ForecastLineItem>) => {
    try {
      const { error } = await supabase
        .from('forecast_line_items')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await loadAll();
    } catch (error) {
      console.error('Error updating line item:', error);
      throw error;
    }
  };

  const deleteLineItem = async (id: string) => {
    try {
      const { error } = await supabase.from('forecast_line_items').delete().eq('id', id);

      if (error) throw error;
      await loadAll();
    } catch (error) {
      console.error('Error deleting line item:', error);
      throw error;
    }
  };

  // Overrides
  const setOverride = async (lineItemId: string, yearIndex: number, amount: number, reason?: string) => {
    try {
      const { error } = await supabase.from('forecast_overrides').upsert({
        line_item_id: lineItemId,
        year_index: yearIndex,
        override_amount: amount,
        reason: reason || null
      });

      if (error) throw error;
      await loadAll();
    } catch (error) {
      console.error('Error setting override:', error);
      throw error;
    }
  };

  const deleteOverride = async (lineItemId: string, yearIndex: number) => {
    try {
      const { error } = await supabase
        .from('forecast_overrides')
        .delete()
        .eq('line_item_id', lineItemId)
        .eq('year_index', yearIndex);

      if (error) throw error;
      await loadAll();
    } catch (error) {
      console.error('Error deleting override:', error);
      throw error;
    }
  };

  return {
    scenario,
    dimensions,
    dimensionValues,
    lineItems,
    overrides,
    calculation,
    loading,
    loadAll,
    createDimension,
    updateDimension,
    deleteDimension,
    setDimensionValue,
    createLineItem,
    updateLineItem,
    deleteLineItem,
    setOverride,
    deleteOverride
  };
}
